<template>
  <VDialog
    :value="visible"
    width="425"
    @click:outside="$emit('closeDialog')"
  >
    <VCard
      :loading="isLoading"
      class="p-0 shadow-none border-radius-8 px-4 py-4 position-relative"
    >
      <template #progress>
        <SSpinner :dialog="true" />
      </template>
      <VCardTitle class="p-0 d-flex justify-content-between align-center position-relative">
        <div>
          <span class="heading-6">
            {{ $t('settings.deleteAccount.title') }}
          </span>
        </div>
        <VBtn
          icon
          class="grey-lighten-4 box-shadow-elevation-6"
          @click="$emit('closeDialog')"
        >
          <VIcon color="textPrimary">mdi-close</VIcon>
        </VBtn>
      </VCardTitle>
      <ValidationObserver
        ref="changePasswordForm"
        v-slot="{ invalid }"
        name="changePasswordForm"
        tag="div"
      >
        <VCardText class="p-0 pt-6">
          <div>
            <p class="body-1 textPrimary--text">
              {{ $t('settings.deleteAccount.bodyText1') }}
              <span v-if="plannedBookingsCount" v-html="getPlannedBookingsDisclaimer" />
            </p>
          </div>
          <div>
            <p class="body-1 textPrimary--text">
              {{ $t('settings.deleteAccount.bodyText3') }}
            </p>
          </div>
          <div>
            <p class="body-1 textPrimary--text">
              {{ $t('settings.deleteAccount.bodyText4') }}
            </p>
          </div>
          <div class="card-text-body">
            <ValidationProvider
              v-slot="{ errors, ariaInput }"
              ref="password"
              name="password"
              :rules="{ required: true, min: 6, max: 40, withoutSpaces: /^\S*$/ }"
              :allowFalse="false"
              mode="aggressive"
            >
              <div class="pt-6">
                <VTextField
                  v-bind="ariaInput"
                  v-model="password"
                  class="body-1 pt-0"
                  :append-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  :type="showPassword ? 'text' : 'password'"
                  :error="!!errors.length || !isPasswordCorrect"
                  :errorMessages="(!isPasswordCorrect) ? $t('authForm.errors.incorrectPassword') : validationErrorResolve(errors[0]) "
                  :hide-details="!errors.length && isPasswordCorrect"
                  :label="$t('settings.deleteAccount.password')"
                  @input="isPasswordCorrect = true"
                  @click:append="showPassword = !showPassword"
                />
              </div>
            </ValidationProvider>
          </div>
        </VCardText>
        <VCardActions class="px-0 pb-0 pt-4">
          <div class="d-flex align-center justify-content-between w-100">
            <div class="w-50 pr-2">
            <VBtn
              v-blur
              class="w-100 body-2 border-radius-8"
              outlined
              large
              color="textTertiary"
              @click="$emit('closeDialog')"
            >
              {{ $t('settings.deleteAccount.cancel') }}
            </VBtn>
            </div>
            <div class="w-50 pl-2">
              <VBtn
                v-blur
                class="w-100 body-2 border-radius-8"
                dark
                large
                :class="{'disabled': invalid }"
                color="error"
                @click="sendDeleteAccountRequest"
              >
                {{ $t('settings.deleteAccount.delete') }}
              </VBtn>
            </div>
          </div>
        </VCardActions>
      </ValidationObserver>
    </VCard>
  </VDialog>
</template>

<script lang="ts">
export default {
  name: 'DeleteAccountDialog',
  // Kept in Options data so template ref="password" stays on $refs (v-model uses this).
  data() {
    return {
      password: '',
    };
  },
};
</script>

<script setup lang="ts">
import { computed, ref } from 'vue';
import SUserService from '@slg/web-customer-shared/src/services/UserService';
import BookingsService from '@slg/web-customer-shared/src/services/BookingsService';
import SSpinner from '@slg/web-customer-shared/src/components/common/SSpinner';
import { useGlobalErrorDialog, useI18n, useStore, useValidationError, useVm } from '../../../composables';

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
    default: false,
  },
  userPhone: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['onDeleteAccountRequestedSuccess']);

const { validationErrorResolve } = useValidationError();
const { setGlobalError } = useGlobalErrorDialog();
const { t } = useI18n();
const store = useStore();
const vm = useVm() as { password: string };

const isLoading = ref(false);
const showPassword = ref(false);
const isPasswordCorrect = ref(true);
const plannedBookingsCount = ref(0);

const getPlannedBookingsDisclaimer = computed(() => {
  const part1 = t('settings.deleteAccount.bodyText2Part1', { plannedBookingsCount: plannedBookingsCount.value });
  const part2 = plannedBookingsCount.value === 1 || plannedBookingsCount.value % 10 === 1
    ? t('settings.deleteAccount.bodyText2Part2Singular')
    : t('settings.deleteAccount.bodyText2Part2Plural');
  const part3 = t('settings.deleteAccount.bodyText2Part3');
  return [part1, part2, part3].join(' ');
});

async function loadPlannedBookingsCount() {
  try {
    const { data } = await BookingsService.getBookingsCount();
    plannedBookingsCount.value = data.planned || 0;
  } catch (error) {
    console.log(error);
  }
}

loadPlannedBookingsCount();

async function sendDeleteAccountRequest() {
  isLoading.value = true;
  try {
    await SUserService.signIn({
      customerAppType: 'WEB',
      phone: props.userPhone,
      password: vm.password,
    });

    await SUserService.requestAccountDeletion();
    await store.dispatch('auth/populateUserProfileInfo');
    emit('onDeleteAccountRequestedSuccess');
  } catch (e) {
    if ((e as any).status !== 401 && (e as any).status !== 403) {
      await setGlobalError(e);
    }

    if ((e as any).status === 403) {
      isPasswordCorrect.value = false;
    }
  }
  isLoading.value = false;
}
</script>
